/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { useCallback, useEffect, useState } from 'react';
import moment from 'moment-timezone';

const usePricing = (product, selectedTime, occurrenceDates, localUpdatedTimes, searchCriteria) => {
  const [checkingDiscounts, setCheckingDiscounts] = useState(false);

  const getPricePerHour = useCallback(() => {
    if (!product || !product.price) {
      return 0;
    }
    return parseFloat(product.price?.priceAmt) || 0;
  }, [product]);

  const getDuration = useCallback(() => {
    const datesToUse = occurrenceDates.length > 0 ? occurrenceDates : [searchCriteria?.date];

    if (datesToUse.length > 0) {
      const firstDate = datesToUse[0];
      const timeData = localUpdatedTimes[firstDate] || selectedTime;

      if (timeData?.duration) {
        return parseFloat(timeData.duration);
      }

      if (selectedTime?.start && selectedTime?.end) {
        const start = moment(`2000-01-01T${selectedTime.start}`);
        const end = moment(`2000-01-01T${selectedTime.end}`);
        const durationHours = end.diff(start, 'minutes') / 60;
        return parseFloat(durationHours.toFixed(2));
      }

      return parseFloat(selectedTime?.duration) || 1;
    }

    if (selectedTime?.start && selectedTime?.end) {
      const start = moment(`2000-01-01T${selectedTime.start}`);
      const end = moment(`2000-01-01T${selectedTime.end}`);
      const durationHours = end.diff(start, 'minutes') / 60;
      return parseFloat(durationHours.toFixed(2));
    }

    return parseFloat(selectedTime?.duration) || 1;
  }, [localUpdatedTimes, occurrenceDates, searchCriteria?.date, selectedTime]);

  const calculateTotalPrice = () => {
    const pricePerHour = getPricePerHour();
    const datesToUse = occurrenceDates.length > 0 ? occurrenceDates : [searchCriteria?.date];

    let total = 0;
    datesToUse.forEach(date => {
      const timeData = localUpdatedTimes[date] || selectedTime;

      let duration;
      if (timeData?.duration) {
        duration = parseFloat(timeData.duration);
      } else if (selectedTime?.start && selectedTime?.end) {
        const start = moment(`2000-01-01T${selectedTime.start}`);
        const end = moment(`2000-01-01T${selectedTime.end}`);
        duration = end.diff(start, 'minutes') / 60;
      } else {
        duration = parseFloat(selectedTime?.duration) || 1;
      }

      total += pricePerHour * duration;
    });

    return parseFloat(total.toFixed(2));
  };

  const getQuantityForDate = (date) => {
    const timeData = localUpdatedTimes[date] || selectedTime;
    
    if (timeData?.duration) {
      return Math.round(parseFloat(timeData.duration));
    }
    
    if (selectedTime?.start && selectedTime?.end) {
      const start = moment(`2000-01-01T${selectedTime.start}`);
      const end = moment(`2000-01-01T${selectedTime.end}`);
      const durationHours = end.diff(start, 'minutes') / 60;
      return Math.round(durationHours);
    }
    
    return Math.round(parseFloat(selectedTime?.duration)) || 1;
  };

  const displayDuration = useCallback(() => {
    const duration = getDuration();
    if (duration % 1 === 0) {
      return `${duration} ${duration === 1 ? 'hour' : 'hours'}`;
    } else {
      return `${duration.toFixed(2)} hours`;
    }
  }, [getDuration]);

  useEffect(() => {
    const pricePerHour = getPricePerHour();
    localStorage.setItem(
      "bookingSummary",
      `${displayDuration()} × $${pricePerHour.toFixed(2)}`
    );
  }, [displayDuration, getPricePerHour]);

  return {
    pricePerHour: getPricePerHour(),
    duration: getDuration(),
    totalPrice: calculateTotalPrice(),
    getQuantityForDate,
    displayDuration,
    checkingDiscounts,
    setCheckingDiscounts
  };
};

export default usePricing;
